import type { SupabaseClient } from "@supabase/supabase-js";

import type { PolygonFeatureCollection } from "./map-points";

// PRD-001Y Y2 — site property boundaries (layer L0). One active GeoJSON Polygon per site, stored
// server-side as jsonb (no PostGIS) and rendered as a muted fill+stroke below the checkpoint
// layers. Reads come from public.management_site_boundaries (management-scoped); writes go through
// the schedule.manage-gated save_site_boundary / clear_site_boundary RPCs. Pure parsers + GeoJSON
// builders keep the editor and map on a unit-tested core.

/** A linear ring of [lng, lat] positions (GeoJSON order), first == last, >= 4 positions. */
export type Ring = [number, number][];

export interface SiteBoundary {
  siteBoundaryId: string;
  siteId: string;
  siteName: string | null;
  label: string;
  source: string;
  ring: Ring;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function isLngLat(value: unknown): value is [number, number] {
  return (
    Array.isArray(value) &&
    value.length >= 2 &&
    typeof value[0] === "number" &&
    typeof value[1] === "number" &&
    Number.isFinite(value[0]) &&
    Number.isFinite(value[1]) &&
    value[0] >= -180 &&
    value[0] <= 180 &&
    value[1] >= -90 &&
    value[1] <= 90
  );
}

/** Extract a valid outer ring from a raw GeoJSON Polygon `coordinates` value, or null. */
export function ringFromCoordinates(coordinates: unknown): Ring | null {
  if (!Array.isArray(coordinates) || coordinates.length < 1) {
    return null;
  }
  const outer = coordinates[0];
  if (!Array.isArray(outer) || outer.length < 4) {
    return null;
  }
  const ring: Ring = [];
  for (const position of outer) {
    if (!isLngLat(position)) {
      return null;
    }
    ring.push([position[0], position[1]]);
  }
  return ring;
}

/** Map a management_site_boundaries row → SiteBoundary, or null when the polygon is unusable. */
export function mapSiteBoundaryRow(raw: Record<string, unknown>): SiteBoundary | null {
  const geojson = raw.boundary_geojson;
  if (geojson === null || typeof geojson !== "object") {
    return null;
  }
  const geo = geojson as Record<string, unknown>;
  if (geo.type !== "Polygon") {
    return null;
  }
  const ring = ringFromCoordinates(geo.coordinates);
  if (ring === null) {
    return null;
  }
  return {
    siteBoundaryId: asString(raw.site_boundary_id) ?? "",
    siteId: asString(raw.site_id) ?? "",
    siteName: asString(raw.site_name),
    label: asString(raw.label) ?? "Property",
    source: asString(raw.source) ?? "drawn",
    ring,
  };
}

/** GeoJSON FeatureCollection of site boundary polygons for the CAD map L0 layer. */
export function toSiteBoundaryFeatureCollection(
  boundaries: readonly SiteBoundary[],
): PolygonFeatureCollection {
  return {
    type: "FeatureCollection",
    features: boundaries.map((boundary) => ({
      type: "Feature",
      geometry: { type: "Polygon", coordinates: [boundary.ring] },
      properties: {
        id: boundary.siteBoundaryId,
        site: boundary.siteName ?? "",
        label: boundary.label,
      },
    })),
  };
}

/** Drop consecutive duplicates and a trailing close duplicate from an in-progress draw ring. */
export function normalizeDrawRing(ring: Ring): Ring {
  if (ring.length === 0) {
    return ring;
  }
  const normalized: Ring = [];
  for (const point of ring) {
    const previous = normalized[normalized.length - 1];
    if (previous !== undefined && previous[0] === point[0] && previous[1] === point[1]) {
      continue;
    }
    normalized.push(point);
  }
  if (normalized.length >= 2) {
    const first = normalized[0]!;
    const last = normalized[normalized.length - 1]!;
    if (first[0] === last[0] && first[1] === last[1]) {
      normalized.pop();
    }
  }
  return normalized;
}

/** Close a ring (append the first position) when the caller left it open. */
export function closeRing(ring: Ring): Ring {
  if (ring.length < 3) {
    return ring;
  }
  const first = ring[0]!;
  const last = ring[ring.length - 1]!;
  if (first[0] === last[0] && first[1] === last[1]) {
    return ring;
  }
  return [...ring, [first[0], first[1]]];
}

/** A GeoJSON Polygon geometry payload for the save RPC, from a (possibly open) ring. */
export function ringToPolygonGeoJson(ring: Ring): { type: "Polygon"; coordinates: [Ring] } {
  return { type: "Polygon", coordinates: [closeRing(ring)] };
}

type LineFeatureCollection = GeoJSON.FeatureCollection<GeoJSON.LineString>;
type PointFeatureCollection = GeoJSON.FeatureCollection<GeoJSON.Point>;

/** Open-ring polyline for in-progress boundary drawing (MapLibre line layer). */
export function toDraftBoundaryLineFeatureCollection(ring: Ring): LineFeatureCollection {
  if (ring.length < 2) {
    return { type: "FeatureCollection", features: [] };
  }
  return {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        geometry: { type: "LineString", coordinates: ring },
        properties: {},
      },
    ],
  };
}

/** Closed polygon preview once the operator has placed at least three corners. */
export function toDraftBoundaryFillFeatureCollection(ring: Ring): PolygonFeatureCollection {
  if (ring.length < 3) {
    return { type: "FeatureCollection", features: [] };
  }
  return {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        geometry: { type: "Polygon", coordinates: [closeRing(ring)] },
        properties: {},
      },
    ],
  };
}

/** Vertex dots so the first click is visible before a line segment exists. */
export function toDraftBoundaryVertexFeatureCollection(ring: Ring): PointFeatureCollection {
  return {
    type: "FeatureCollection",
    features: ring.map((position, index) => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: position },
      properties: { index: String(index) },
    })),
  };
}

export type ParseResult = { ok: true; ring: Ring } | { ok: false; error: string };

/**
 * Parse a pasted GeoJSON string into a closed outer ring (import v1). Accepts a bare Polygon
 * geometry, a Feature wrapping a Polygon, or a FeatureCollection whose first Polygon feature is
 * used. Returns a friendly error string on anything malformed — never throws.
 */
export function parseGeoJsonPolygon(text: string): ParseResult {
  const trimmed = text.trim();
  if (trimmed.length === 0) {
    return { ok: false, error: "Paste a GeoJSON polygon to import." };
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return { ok: false, error: "That is not valid JSON." };
  }
  const geometry = extractPolygonGeometry(parsed);
  if (geometry === null) {
    return { ok: false, error: "No GeoJSON Polygon found. Provide a Polygon geometry, Feature, or FeatureCollection." };
  }
  const ring = ringFromCoordinates((geometry as Record<string, unknown>).coordinates);
  if (ring === null) {
    return { ok: false, error: "The polygon ring needs at least four valid [lng, lat] positions." };
  }
  return { ok: true, ring: closeRing(ring) };
}

function extractPolygonGeometry(node: unknown): unknown {
  if (node === null || typeof node !== "object") {
    return null;
  }
  const record = node as Record<string, unknown>;
  if (record.type === "Polygon") {
    return record;
  }
  if (record.type === "Feature") {
    return extractPolygonGeometry(record.geometry);
  }
  if (record.type === "FeatureCollection" && Array.isArray(record.features)) {
    for (const feature of record.features) {
      const geometry = extractPolygonGeometry(feature);
      if (geometry !== null) {
        return geometry;
      }
    }
  }
  return null;
}

const BOUNDARY_LIMIT = 500;

export async function fetchSiteBoundaries(
  client: SupabaseClient,
  accountId: string,
): Promise<SiteBoundary[]> {
  const { data, error } = await client
    .from("management_site_boundaries")
    .select("site_boundary_id, account_id, site_id, site_name, label, boundary_geojson, source, updated_at")
    .eq("account_id", accountId)
    .limit(BOUNDARY_LIMIT);
  if (error !== null) {
    throw new Error("Could not load site boundaries.");
  }
  if (!Array.isArray(data)) {
    return [];
  }
  return data
    .map((row) => mapSiteBoundaryRow(row as Record<string, unknown>))
    .filter((boundary): boundary is SiteBoundary => boundary !== null);
}

export type BoundaryWriteOutcome = { ok: true } | { ok: false; reason: string };

export const SAFE_BOUNDARY_WRITE_FAILURE = "Couldn't save that site boundary — please try again.";

// Contract-stable, value-free sentences from the boundary RPCs; anything else fails closed.
const KNOWN_BOUNDARY_FAILURES: ReadonlySet<string> = new Set([
  "Account id is required to save a site boundary.",
  "Site id is required to save a site boundary.",
  "Authentication is required to save a site boundary.",
  "Authentication is required to clear a site boundary.",
  "Caller is not authorized to manage schedules for this account.",
  "Site was not found for this account.",
  "Site boundary source is not supported.",
  "Site boundary must be a GeoJSON Polygon with a closed ring of at least four positions.",
  "Account id and site id are required to clear a site boundary.",
]);

function rpcErrorMessage(error: unknown): string {
  if (error === null || error === undefined) {
    return "";
  }
  if (typeof error === "string") {
    return error;
  }
  if (typeof error !== "object") {
    return "";
  }
  const record = error as Record<string, unknown>;
  if (typeof record.message === "string") {
    return record.message;
  }
  if (typeof record.details === "string") {
    return record.details;
  }
  return "";
}

function boundaryReason(error: unknown): string {
  const message = rpcErrorMessage(error);
  if (KNOWN_BOUNDARY_FAILURES.has(message)) {
    return message;
  }
  if (message.includes("site_boundaries_geojson_polygon")) {
    return "Site boundary must be a GeoJSON Polygon with a closed ring of at least four positions.";
  }
  if (message.includes("violates check constraint")) {
    return "That boundary shape is not valid. Redraw from scratch with at least three corners.";
  }
  if (message.includes("permission denied for function app_private.save_site_boundary_for_caller")) {
    return "Site boundary save is not enabled for your account yet. Contact support if this persists.";
  }
  if (message.includes("permission denied for function app_private.clear_site_boundary_for_caller")) {
    return "Site boundary clear is not enabled for your account yet. Contact support if this persists.";
  }
  if (message.includes("permission denied")) {
    return "You do not have permission to save this site boundary.";
  }
  return SAFE_BOUNDARY_WRITE_FAILURE;
}

export async function saveSiteBoundary(
  client: SupabaseClient,
  accountId: string,
  siteId: string,
  ring: Ring,
  options: { label?: string; source?: string } = {},
): Promise<BoundaryWriteOutcome> {
  try {
    const normalizedRing = normalizeDrawRing(ring);
    const { error } = await client.rpc("save_site_boundary", {
      p_account_id: accountId,
      p_site_id: siteId,
      p_label: options.label ?? "Property",
      p_boundary_geojson: ringToPolygonGeoJson(normalizedRing),
      p_source: options.source ?? "drawn",
    });
    return error === null ? { ok: true } : { ok: false, reason: boundaryReason(error) };
  } catch {
    return { ok: false, reason: SAFE_BOUNDARY_WRITE_FAILURE };
  }
}

export async function clearSiteBoundary(
  client: SupabaseClient,
  accountId: string,
  siteId: string,
): Promise<BoundaryWriteOutcome> {
  try {
    const { error } = await client.rpc("clear_site_boundary", {
      p_account_id: accountId,
      p_site_id: siteId,
    });
    return error === null ? { ok: true } : { ok: false, reason: boundaryReason(error) };
  } catch {
    return { ok: false, reason: SAFE_BOUNDARY_WRITE_FAILURE };
  }
}
